import {
  Injectable,
  OnDestroy,
  Renderer2,
  RendererFactory2,
} from '@angular/core';
import { Observable, Subject, take, tap } from 'rxjs';
import { NzModalService } from 'ng-zorro-antd/modal';
import { Note } from '../../../features/manage-settings/notes/notes';
import { NoteService } from '../../../features/manage-settings/notes/note.service';
import { FEATURE_TO_DRAWER_MAP } from '../../constants/drawer-notes';
import { DomUtilsService } from './dom-utils.service';

@Injectable({
  providedIn: 'root',
})
export class HelpIconManager implements OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private readonly renderer: Renderer2;
  notes$: Observable<Note[]>;

  private readonly helpIconSvg = `
    <svg width="1em" height="1em" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M8.09 8C8.3251 7.33167 8.78915 6.76811 9.39995 6.40913C10.0108 6.05016 10.7289 5.91894 11.4272 6.03871C12.1255 6.15849 12.7588 6.52152 13.2151 7.06353C13.6713 7.60553 13.9211 8.29152 13.92 9C13.92 11 10.92 12 10.92 12M11 16H11.01M21 11C21 16.5228 16.5228 21 11 21C5.47715 21 1 16.5228 1 11C1 5.47715 5.47715 1 11 1C16.5228 1 21 5.47715 21 11Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>
  `;

  constructor(
    private readonly domUtils: DomUtilsService,
    private readonly modalService: NzModalService,
    private readonly noteService: NoteService,
    rendererFactory: RendererFactory2
  ) {
    this.renderer = rendererFactory.createRenderer(null, null);
    this.notes$ = this.noteService.notes$;
    this.addGlobalStyles();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  addHelpIconToDrawer(drawerId: string): void {
    if (!drawerId) return;

    setTimeout(() => {
      try {
        // Use the drawer index to find the element
        const drawer = this.domUtils.findLastDrawerElement();
        if (!drawer) return;

        const header = drawer.querySelector('.ant-drawer-header');
        if (!header) return;

        if (header.querySelector('.drawer-help-icon')) return;

        this.notes$
          .pipe(
            take(1),
            tap((notes: Note[]) => {
              if (!notes || !Array.isArray(notes)) return;

              const activeFeature = notes.find(
                (item: Note) =>
                  FEATURE_TO_DRAWER_MAP[item?.featureId] === drawerId &&
                  item?.statusId === 1
              );

              if (activeFeature) {
                const helpIcon = this.renderer.createElement('div');
                helpIcon.className = 'drawer-help-icon';
                helpIcon.innerHTML = this.helpIconSvg;

                helpIcon.setAttribute('title', 'View Notes');

                helpIcon.addEventListener('click', (event: MouseEvent) => {
                  event.stopPropagation();
                  this.showHelpModal(activeFeature);
                });

                this.renderer.appendChild(header, helpIcon);
              }
            })
          )
          .subscribe({
            error: err =>
              console.error('Error adding help icon to drawer:', err),
          });
      } catch (error) {
        console.error(`Error adding help icon to drawer: ${error}`);
      }
    }, 50);
  }

  private showHelpModal(feature: Note): void {
    if (!feature) return;

    try {
      const helpContent = feature.description || 'No description available';

      const modalRef = this.modalService.create({
        nzTitle: feature.feature || 'Help',
        nzContent: helpContent,
        nzCentered: true,
        nzWidth: 500,
        nzMaskClosable: true,
        nzClassName: 'help-modal-container',
        nzBodyStyle: {
          padding: '24px',
          fontSize: '14px',
          lineHeight: '1.6',
          color: '#333',
        },
        nzFooter: [
          {
            label: 'Close',
            type: 'primary',
            shape: 'round',
            onClick: () => {
              modalRef.close();
            },
          },
        ],
      });
    } catch (error) {
      console.error(`Error showing help modal: ${error}`);
    }
  }

  private addGlobalStyles(): void {
    const style = this.renderer.createElement('style');
    style.textContent = `
      .drawer-help-icon {
        border: 1px solid rgb(var(--gray-300));
        border-radius: 8px;
        width: 36px;
        height: 36px;
        transition: all 0.2s ease-in;
        color: rgb(var(--gray-500));
        position: absolute;
        right: 70px;
        top: 24px;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        margin-right: 16px;
      }
    `;
    this.renderer.appendChild(document.head, style);
  }
}
